import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faClipboardList,
  faCubes,
  faExchangeAlt,
  faHeadset,
  faHouse,
  faSliders,
  faSlidersH,
  faWarehouse,
} from '@fortawesome/free-solid-svg-icons'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import { PlatformDataType } from 'src/interfaces/Platform.interface'
import { checkIsActive, KTIcon } from 'src/marketConnect/helpers'
import { getURL } from 'src/redux/actions/authActions'
import { setProductTablePaginationData, setReset, setTheRangeId } from 'src/redux/slices/productSlice'
import { userStaffMenus } from 'src/utils/constants'
import styles from './OmsSidebarMenu.module.scss'
import { useFreeTrialDeferredOnboarding } from 'src/hooks/useFreeTrialDeferredOnboarding'

const formatPlatformTitle = (displayName: string, platformName?: string) => {
  const normalizedDisplay = String(displayName ?? '').trim().toUpperCase()
  const normalizedName = String(platformName ?? '').trim().toUpperCase()
  if (
    normalizedName === 'COMMERCIUMOMS' ||
    normalizedDisplay === 'COMMERCIUMOMS' ||
    normalizedDisplay === 'COMMERCIUM OMS PRODUCTS' ||
    normalizedDisplay.includes('COMMERCIUM OMS')
  ) {
    return 'Master Products'
  }
  return displayName + ' Products'
}

const SESSION_KEYS = {
  products: 'oms-sidebar-products-open',
  inventory: 'oms-sidebar-inventory-open',
  sales: 'oms-sidebar-sales-open',
  tools: 'oms-sidebar-tools-open',
} as const

type CollapseKey = keyof typeof SESSION_KEYS

const readSessionBool = (key: string, fallback: boolean) => {
  try {
    const v = sessionStorage.getItem(key)
    if (v === 'true') return true
    if (v === 'false') return false
  } catch {
    /* ignore */
  }
  return fallback
}

const writeSessionBool = (key: string, value: boolean) => {
  try {
    sessionStorage.setItem(key, String(value))
  } catch {
    /* ignore */
  }
}

const closeAllSidebarSections = () => {
  Object.values(SESSION_KEYS).forEach((key) => writeSessionBool(key, false))
}

type StaticMenuItem = {
  title: string
  to: string
  icon?: string
  alternateIcon?: typeof faSliders
}

type OmsSidebarMenuProps = {
  isUserStaff: boolean
  shouldShowDirectCustomerMenus: boolean
}

const OmsSidebarMenu: React.FC<OmsSidebarMenuProps> = ({
  isUserStaff,
  shouldShowDirectCustomerMenus,
}) => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()
  const { fetchedConnectedPlatformData } = useAppSelector((state) => state.fetchConnectedPlatform)
  const { fetchedUserAccountData } = useAppSelector((state) => state.userAccountData)

  const parentUserRole = fetchedUserAccountData?.parent_user_role
  const isOmsStoreAccess =
    fetchedUserAccountData?.userAdditionalInfo?.platform_connection_priority === 'OMS' || false

  const [primaryStorePlatforms, setPrimaryStorePlatforms] = useState<PlatformDataType[]>([])
  const [secondaryStorePlatforms, setSecondaryStorePlatforms] = useState<PlatformDataType[]>([])
  const [accountingStorePlatforms, setAccountingStorePlatforms] = useState<PlatformDataType[]>([])
  const [shippingStorePlatforms, setShippingStorePlatforms] = useState<PlatformDataType[]>([])
  const [omsStorePlatforms, setOmsStorePlatforms] = useState<PlatformDataType[]>([])
  const [loader, setLoader] = useState(false)

  const [productsOpen, setProductsOpen] = useState(() => readSessionBool(SESSION_KEYS.products, false))
  const [inventoryOpen, setInventoryOpen] = useState(() => readSessionBool(SESSION_KEYS.inventory, false))
  const [salesOpen, setSalesOpen] = useState(() => readSessionBool(SESSION_KEYS.sales, false))
  const [toolsOpen, setToolsOpen] = useState(() => readSessionBool(SESSION_KEYS.tools, false))

  const { isDeferredFreeTrialOnboarding, handleDeferredNavClick } = useFreeTrialDeferredOnboarding()
  const isSubscriptionUnlocked =
    !!fetchedUserAccountData?.is_subscribed && !!fetchedUserAccountData?.isOnboardingProcessCompleted
  const isNavUnlocked = isSubscriptionUnlocked || isDeferredFreeTrialOnboarding

  const isSuperAdmin =
    fetchedUserAccountData?.is_superuser ||
    fetchedUserAccountData?.user_type === 'SUPERADMIN' ||
    fetchedUserAccountData?.account_type === 'SUPERADMIN'

  const showProductsModule = fetchedUserAccountData?.isEnableProductModule || isSuperAdmin

  useEffect(() => {
    if (fetchedConnectedPlatformData && fetchedConnectedPlatformData.length > 0) {
      setOmsStorePlatforms(fetchedConnectedPlatformData.filter((platform) => platform.isOmsStore))
      setPrimaryStorePlatforms(fetchedConnectedPlatformData.filter((platform) => platform.isMainStore))
      setAccountingStorePlatforms(
        fetchedConnectedPlatformData.filter((platform) => platform.isAccountingStore)
      )
      setShippingStorePlatforms(
        fetchedConnectedPlatformData.filter((platform) => platform.isShippingStore)
      )
      setSecondaryStorePlatforms(
        fetchedConnectedPlatformData.filter(
          (platform) =>
            !platform.isMainStore &&
            !platform.isAccountingStore &&
            !platform.isShippingStore &&
            !platform.isOmsStore
        )
      )
    } else {
      setPrimaryStorePlatforms([])
      setAccountingStorePlatforms([])
      setShippingStorePlatforms([])
      setSecondaryStorePlatforms([])
      setOmsStorePlatforms([])
    }
  }, [fetchedConnectedPlatformData])

  const channelListingPlatforms = useMemo(() => {
    const items: { key: string; platform: PlatformDataType; title: string }[] = []

    primaryStorePlatforms.forEach((platform) => {
      items.push({ key: `primary-${platform.id}`, platform, title: 'Source Store Products' })
    })
    accountingStorePlatforms.forEach((platform) => {
      items.push({ key: `accounting-${platform.id}`, platform, title: 'Accounting Store Products' })
    })
    shippingStorePlatforms.forEach((platform) => {
      items.push({ key: `shipping-${platform.id}`, platform, title: 'Shipping Store Products' })
    })
    secondaryStorePlatforms.forEach((platform) => {
      items.push({
        key: `secondary-${platform.id}`,
        platform,
        title: formatPlatformTitle(platform.display_name, platform.name),
      })
    })

    return items
  }, [
    primaryStorePlatforms,
    accountingStorePlatforms,
    shippingStorePlatforms,
    secondaryStorePlatforms,
  ])

  const managementItems: StaticMenuItem[] = useMemo(
    () => [
      { title: 'List Products', to: '/listing-profiles', icon: 'questionnaire-tablet' },
      { title: 'Kits & Bundles', to: '/bundles', alternateIcon: faCubes },
      { title: 'Price Logs', to: '/price-logs', icon: 'dollar' },
    ],
    []
  )

  const inventoryItems: StaticMenuItem[] = useMemo(
    () => [
      ...(isOmsStoreAccess
        ? [
            { title: 'Inventory Adjustment', to: '/inventory-adjustment', alternateIcon: faSlidersH },
            { title: 'Inventory Transfer', to: '/inventory-transfer', alternateIcon: faExchangeAlt },
          ]
        : []),
      { title: 'Inventory Logs', to: '/inventory-logs', alternateIcon: faWarehouse },
    ],
    [isOmsStoreAccess]
  )

  useEffect(() => {
    const csvPlatform = secondaryStorePlatforms.find(
      (platform) => platform.name === 'THERANGEPLUS' && process.env.REACT_APP_ENV === 'staging'
    )
    if (csvPlatform) {
      dispatch(setTheRangeId(csvPlatform.id))
    }
  }, [secondaryStorePlatforms, dispatch])

  const productRoutes = useMemo(() => {
    const routes: string[] = []
    if (!isUserStaff) {
      omsStorePlatforms.forEach((p) => {
        routes.push(`/listing-product-table/${p.platform_info?.id}/${p.id}`)
      })
    }
    channelListingPlatforms.forEach(({ platform }) => {
      routes.push(`/listing-product-table/${platform.platform_info?.id}/${platform.id}`)
    })
    managementItems.forEach((item) => routes.push(item.to))
    if (
      !isUserStaff &&
      secondaryStorePlatforms.some(
        (platform) => platform.name === 'THERANGEPLUS' && process.env.REACT_APP_ENV === 'staging'
      )
    ) {
      routes.push('/products-files')
    }
    return routes
  }, [
    isUserStaff,
    omsStorePlatforms,
    channelListingPlatforms,
    managementItems,
    secondaryStorePlatforms,
  ])

  const inventoryRoutes = useMemo(
    () => inventoryItems.map((item) => item.to),
    [inventoryItems]
  )

  useEffect(() => {
    const isDashboard = checkIsActive(pathname, '/dashboard')

    if (isDashboard) {
      setProductsOpen(false)
      setInventoryOpen(false)
      setSalesOpen(false)
      setToolsOpen(false)
      closeAllSidebarSections()
      return
    }

    if (productRoutes.some((route) => checkIsActive(pathname, route))) {
      setProductsOpen(true)
    }
    if (inventoryRoutes.some((route) => checkIsActive(pathname, route))) {
      setInventoryOpen(true)
    }
    if (checkIsActive(pathname, '/orders-all')) {
      setSalesOpen(true)
    }
    if (checkIsActive(pathname, '/rule-engine')) {
      setToolsOpen(true)
    }
  }, [pathname, productRoutes, inventoryRoutes])

  const productsSectionActive = productRoutes.some((route) => checkIsActive(pathname, route))
  const inventorySectionActive = inventoryRoutes.some((route) => checkIsActive(pathname, route))
  const salesSectionActive = checkIsActive(pathname, '/orders-all')
  const toolsSectionActive = checkIsActive(pathname, '/rule-engine')

  const toggleSection = useCallback((key: CollapseKey, current: boolean, setter: (v: boolean) => void) => {
    const next = !current
    setter(next)
    writeSessionBool(SESSION_KEYS[key], next)
  }, [])

  const resetProductTable = useCallback(() => {
    dispatch(
      setProductTablePaginationData({
        page: 1,
        limit: 10,
        search_string: '',
        sort_by: '',
        sort_order: '',
        sync_status: '',
        accounting_sync_status: '',
        shipping_sync_status: '',
        erp_sync_status: '',
        erp_secondary_sync_status: '',
      })
    )
    dispatch(setReset())
  }, [dispatch])

  const getLink = () => {
    setLoader(true)
    dispatch(getURL())
      .then((response) => {
        const redirectUrl = response?.payload?.data?.data?.redirect_url
        if (response?.payload?.data?.status_code === 201 && redirectUrl) {
          window.open(redirectUrl, '_blank')
        }
      })
      .finally(() => setLoader(false))
  }

  const staffHasProductAccess =
    !!parentUserRole?.product_access && parentUserRole.product_access !== 'RESTRICT'

  const staffMenuAllowed = (menu: (typeof userStaffMenus)[0]) => {
    if (menu.permissionKey) {
      if (
        !parentUserRole ||
        !(menu.permissionKey in parentUserRole) ||
        parentUserRole[menu.permissionKey as keyof typeof parentUserRole] === 'RESTRICT'
      ) {
        return false
      }
    }
    if (menu.moduleCheck) {
      return Boolean(fetchedUserAccountData?.[menu.moduleCheck as keyof typeof fetchedUserAccountData])
    }
    return true
  }

  const renderPlatformLink = (key: string, platform: PlatformDataType, title: string) => {
    const to = `/listing-product-table/${platform.platform_info?.id}/${platform.id}`
    const active = checkIsActive(pathname, to)
    const disabled = !isNavUnlocked

    return (
      <Link
        key={key}
        to={to}
        className={clsx(styles.link, active && styles.linkActive, disabled && styles.linkDisabled)}
        onClick={(event) => {
          if (isDeferredFreeTrialOnboarding && to !== '/support') {
            handleDeferredNavClick(event)
            return
          }
          resetProductTable()
        }}
      >
        <span className={clsx('oms-sidebar-link-icon', styles.linkIcon)}>
          {platform.platform_info?.logo_ref ? (
            <img src={platform.platform_info.logo_ref} alt="" className={styles.linkIconImg} />
          ) : (
            <KTIcon iconName="abstract-16" className="fs-4" />
          )}
        </span>
        <span className={clsx('oms-sidebar-link-title', styles.linkTitle)}>{title}</span>
      </Link>
    )
  }

  const renderStaticLink = (item: StaticMenuItem) => {
    const active = checkIsActive(pathname, item.to)
    const disabled = !isNavUnlocked

    return (
      <Link
        key={item.title}
        to={item.to}
        className={clsx(styles.link, active && styles.linkActive, disabled && styles.linkDisabled)}
        onClick={(event) => {
          if (isDeferredFreeTrialOnboarding && item.to !== '/support') {
            handleDeferredNavClick(event)
          }
        }}
      >
        <span className={clsx('oms-sidebar-link-icon', styles.linkIcon)}>
          {item.icon ? (
            <KTIcon iconName={item.icon} className="fs-4" />
          ) : item.alternateIcon ? (
            <FontAwesomeIcon icon={item.alternateIcon} />
          ) : null}
        </span>
        <span className={clsx('oms-sidebar-link-title', styles.linkTitle)}>{item.title}</span>
      </Link>
    )
  }

  const renderRootLink = (
    to: string,
    title: string,
    icon?: typeof faHouse,
    ktIcon?: string,
    onClick?: () => void,
    requiresSubscription = true
  ) => {
    const active = to ? checkIsActive(pathname, to) : false
    const disabled = requiresSubscription && !isNavUnlocked

    if (onClick) {
      return (
        <button
          type="button"
          key={title}
          title={title}
          className={clsx(
            styles.link,
            styles.rootLink,
            'oms-sidebar-root-link',
            active && styles.linkActive,
            active && 'oms-sidebar-item-active',
            (disabled || loader) && styles.linkDisabled
          )}
          onClick={onClick}
          disabled={disabled || loader}
        >
          <span className={clsx('oms-sidebar-link-icon', styles.linkIcon)}>
            {ktIcon ? <KTIcon iconName={ktIcon} className="fs-4" /> : icon ? <FontAwesomeIcon icon={icon} /> : null}
          </span>
          <span className={clsx('oms-sidebar-link-title', styles.linkTitle)}>{title}</span>
        </button>
      )
    }

    return (
      <Link
        key={title}
        to={to}
        title={title}
        className={clsx(
          styles.link,
          styles.rootLink,
          'oms-sidebar-root-link',
          active && styles.linkActive,
          active && 'oms-sidebar-item-active',
          disabled && styles.linkDisabled
        )}
        onClick={(event) => {
          if (isDeferredFreeTrialOnboarding && requiresSubscription && to !== '/support') {
            handleDeferredNavClick(event)
          }
        }}
      >
        <span className={clsx('oms-sidebar-link-icon', styles.linkIcon)}>
          {ktIcon ? (
            <KTIcon iconName={ktIcon} className="fs-4" />
          ) : icon ? (
            <FontAwesomeIcon icon={icon} />
          ) : null}
        </span>
        <span className={clsx('oms-sidebar-link-title', styles.linkTitle)}>{title}</span>
      </Link>
    )
  }

  const renderCollapsibleGroup = (
    key: CollapseKey,
    title: string,
    iconNode: React.ReactNode,
    isOpen: boolean,
    setOpen: (v: boolean) => void,
    children: React.ReactNode,
    isSectionActive = false
  ) => (
    <div className={styles.group}>
      <button
        type="button"
        className={clsx(
          styles.groupHeader,
          'oms-sidebar-group-header',
          isOpen && styles.groupHeaderOpen,
          isSectionActive && styles.groupHeaderActive,
          isSectionActive && 'oms-sidebar-item-active'
        )}
        onClick={() => toggleSection(key, isOpen, setOpen)}
        aria-expanded={isOpen}
        title={title}
      >
        <span className={clsx('oms-sidebar-group-icon', styles.groupIcon)}>{iconNode}</span>
        <span className={clsx('oms-sidebar-group-title', styles.groupTitle)}>{title}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={clsx('oms-sidebar-group-chevron', styles.groupChevron, isOpen && styles.groupChevronOpen)}
        />
      </button>
      {isOpen && <div className={clsx('oms-sidebar-group-body', styles.groupBody)}>{children}</div>}
    </div>
  )

  const renderProductsSection = (includeOmsMaster: boolean) => {
    const hasOmsItems = includeOmsMaster && omsStorePlatforms.length > 0
    const hasChannelItems = channelListingPlatforms.length > 0
    const hasManagement = managementItems.length > 0
    const csvPlatform = secondaryStorePlatforms.find(
      (platform) => platform.name === 'THERANGEPLUS' && process.env.REACT_APP_ENV === 'staging'
    )

    if (!hasOmsItems && !hasChannelItems && !hasManagement && !csvPlatform) {
      return null
    }

    return renderCollapsibleGroup(
      'products',
      'Products',
      <KTIcon iconName="cube-2" className="fs-3" />,
      productsOpen,
      setProductsOpen,
      <>
        {includeOmsMaster &&
          omsStorePlatforms.map((platform) =>
            renderPlatformLink(
              `oms-${platform.id}`,
              platform,
              formatPlatformTitle(platform.display_name, platform.name)
            )
          )}

        {hasChannelItems && (
          <>
            <div className={clsx('oms-sidebar-section-label', styles.sectionLabel)}>Channel Listings</div>
            {channelListingPlatforms.map(({ key, platform, title }) =>
              renderPlatformLink(key, platform, title)
            )}
          </>
        )}

        {csvPlatform && (
          <Link
            key={`csv-${csvPlatform.id}`}
            to="/products-files"
            className={clsx(
              styles.link,
              checkIsActive(pathname, '/products-files') && styles.linkActive,
              !isNavUnlocked && styles.linkDisabled
            )}
            onClick={(event) => {
              if (isDeferredFreeTrialOnboarding) {
                handleDeferredNavClick(event)
              }
            }}
          >
            <span className={clsx('oms-sidebar-link-icon', styles.linkIcon)}>
              <span className="bullet bullet-dot" />
            </span>
            <span className={clsx('oms-sidebar-link-title', styles.linkTitle)}>CSV Download</span>
          </Link>
        )}

        {hasManagement && (
          <>
            <div className={clsx('oms-sidebar-section-label', styles.sectionLabel)}>Management</div>
            {managementItems.map((item) => renderStaticLink(item))}
          </>
        )}
      </>,
      productsSectionActive
    )
  }

  const renderInventorySection = () =>
    renderCollapsibleGroup(
      'inventory',
      'Inventory',
      <FontAwesomeIcon icon={faClipboardList} />,
      inventoryOpen,
      setInventoryOpen,
      inventoryItems.map((item) => renderStaticLink(item)),
      inventorySectionActive
    )

  const renderDirectCustomerMenu = () => (
    <>
      {renderRootLink('/dashboard', 'Dashboard', faHouse)}

      {showProductsModule && (
        <>
          {renderProductsSection(true)}
          {renderInventorySection()}
        </>
      )}

      {renderCollapsibleGroup(
        'sales',
        'Sales',
        <KTIcon iconName="chart-simple-2" className="fs-3" />,
        salesOpen,
        setSalesOpen,
        renderStaticLink({
          title: intl.formatMessage({ id: 'MENU.ORDERS' }),
          to: '/orders-all',
          alternateIcon: faClipboardList,
        }),
        salesSectionActive
      )}

      {renderRootLink('/listing-channels', 'Integrations', undefined, 'fasten')}

      {renderCollapsibleGroup(
        'tools',
        'Tools',
        <KTIcon iconName="wrench" className="fs-3" />,
        toolsOpen,
        setToolsOpen,
        renderStaticLink({ title: 'Automation Rules', to: '/rule-engine', alternateIcon: faSliders }),
        toolsSectionActive
      )}

      {fetchedUserAccountData?.source === 'DISCONNECT' &&
        renderRootLink('', 'Discogs Integration', undefined, 'abstract-44', getLink)}

      {renderRootLink('/support', intl.formatMessage({ id: 'SUPPORT' }), faHeadset, undefined, undefined, false)}
    </>
  )

  const renderStaffMenu = () =>
    userStaffMenus.filter(staffMenuAllowed).map((menu) => {
      if (menu.title === 'Dashboard') {
        return renderRootLink('/dashboard', 'Dashboard', faHouse)
      }

      if (menu.title === 'Products') {
        if (!staffHasProductAccess || !showProductsModule) return null
        return (
          <React.Fragment key="products-inventory">
            {renderProductsSection(false)}
            {renderInventorySection()}
          </React.Fragment>
        )
      }

      if (menu.title === 'Sales') {
        return (
          <React.Fragment key="sales">
            {renderRootLink(
              '/orders-all',
              intl.formatMessage({ id: 'MENU.ORDERS' }),
              faClipboardList,
              undefined,
              undefined,
              false
            )}
          </React.Fragment>
        )
      }

      if (menu.title === 'Integrations') {
        return renderRootLink('/listing-channels', 'Integrations', undefined, 'fasten')
      }

      if (menu.title === 'Tools') {
        return renderRootLink('/rule-engine', 'Automation Rules', faSliders, undefined, undefined, false)
      }

      if (menu.title === 'Support') {
        return renderRootLink('/support', 'Support', faHeadset, undefined, undefined, false)
      }

      return null
    })

  if (!shouldShowDirectCustomerMenus && !(isUserStaff && parentUserRole)) {
    return null
  }

  return (
    <div className={clsx('oms-sidebar-menu', styles.omsSidebarMenu)}>
      {isUserStaff && parentUserRole ? renderStaffMenu() : renderDirectCustomerMenu()}
    </div>
  )
}

export { OmsSidebarMenu }
